// Copyright (c) Bentley Systems
import {
  Badge,
  Button,
  DefaultCell,
  Table,
  Text,
  Tooltip,
} from "@itwin/itwinui-react";
import { CellRendererProps, Column } from "@itwin/itwinui-react/react-table";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../app/hooks";
import { setConfirmation } from "../app/confirmation-slice";
import { useToastNotificationService } from "../Helpers/ToastNotificationService";
import { ClientProjectRow, OnPremProjectKey } from "./Models/on-prem-telemetry-models";

const rowKey = (row: OnPremProjectKey): string =>
  `${row.mqttClientName}::${row.projectId}::${row.iTwinId}`;

type ProjectRunsManagementTableProps = {
  rows: ClientProjectRow[];
  isMutating?: boolean;
  /** Omit both handlers for the read-only single-iTwin view. */
  onSetMuted?: (keys: OnPremProjectKey[], muted: boolean) => Promise<boolean>;
  onDelete?: (keys: OnPremProjectKey[]) => Promise<boolean>;
};

/**
 * One on-prem client's integration project runs, nested inside that client's ExpandableBlock.
 * With mute/delete handlers it is the architects' bulk-management table across iTwins; without
 * them it is a read-only list of a single iTwin's runs, so the iTwin and muted columns are omitted.
 */
const ProjectRunsManagementTable = ({
  rows,
  isMutating = false,
  onSetMuted,
  onDelete,
}: ProjectRunsManagementTableProps) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const isManageable = onSetMuted !== undefined && onDelete !== undefined;

  const [selectedRows, setSelectedRows] = useState<ClientProjectRow[]>([]);

  // Defensive reset: `rows` is re-filtered whenever the parent's health-window changes, which can
  // drop rows the user had selected. Don't rely on the underlying Table to re-sync its own
  // selection state against a brand-new `data` array — clear it explicitly instead.
  useEffect(() => {
    setSelectedRows([]);
  }, [rows]);

  const isBulkOperationDisabled = selectedRows.length === 0 || isMutating;

  const onRowsSelect = (selected: ClientProjectRow[] | undefined) => {
    setSelectedRows(selected ?? []);
  };

  const selectedKeys = (): OnPremProjectKey[] =>
    selectedRows.map((r) => ({ mqttClientName: r.mqttClientName, projectId: r.projectId, iTwinId: r.iTwinId }));

  const handleMute = async (muted: boolean): Promise<void> => {
    if (!onSetMuted) {
      return;
    }
    const success = await onSetMuted(selectedKeys(), muted);
    if (success) {
      setSelectedRows([]);
      showToast({
        status: "positive",
        message: muted ? "Selected project run(s) muted." : "Selected project run(s) unmuted.",
      });
    }
  };

  const handleDelete = (): void => {
    if (!onDelete) {
      return;
    }
    const keys = selectedKeys();
    dispatch(
      setConfirmation({
        isConfirmationOpen: true,
        caption: "Delete project run records?",
        text: `Do you really want to delete ${keys.length} project run record(s)? This cannot be undone.`,
        onOkAction: async () => {
          const success = await onDelete(keys);
          if (success) {
            setSelectedRows([]);
            showToast({
              status: "positive",
              message: `${keys.length} project run record(s) deleted.`,
            });
          }
        },
      }),
    );
  };

  const columns: Column<ClientProjectRow>[] = useMemo(
    () => [
      {
        Header: "Name",
        accessor: (r: ClientProjectRow) => r.projectName ?? r.projectId,
        id: "projectName",
        minWidth: 180,
      },
      ...(isManageable
        ? [
            {
              Header: "iTwin",
              accessor: (r: ClientProjectRow) => r.iTwinId,
              id: "iTwinId",
              minWidth: 180,
            },
          ]
        : []),
      {
        Header: "Last Run",
        accessor: (r: ClientProjectRow) => new Date(r.lastRun).toLocaleString(),
        id: "lastRun",
        width: 200,
      },
      {
        Header: "Status",
        id: "status",
        width: 120,
        cellRenderer: (props: CellRendererProps<ClientProjectRow>) => {
          const row = props.cellProps.row.original;
          return (
            <DefaultCell {...props}>
              <Tooltip
                content={
                  row.lastRunSuccess
                    ? "The latest run of this project on this client succeeded."
                    : "The latest run of this project on this client failed. Check the run's log for details."
                }
              >
                <Badge backgroundColor={row.lastRunSuccess ? "positive" : "negative"}>
                  {row.lastRunSuccess ? "Success" : "Failure"}
                </Badge>
              </Tooltip>
            </DefaultCell>
          );
        },
      },
      ...(isManageable
        ? [
            {
              Header: "Muted",
              id: "isMuted",
              width: 100,
              cellRenderer: (props: CellRendererProps<ClientProjectRow>) => {
                const row = props.cellProps.row.original;
                return (
                  <DefaultCell {...props}>
                    {row.isMuted ? (
                      <Tooltip content="Muted: excluded from this client's health status on this page.">
                        <Badge backgroundColor="hollow">Muted</Badge>
                      </Tooltip>
                    ) : (
                      <Text isMuted>—</Text>
                    )}
                  </DefaultCell>
                );
              },
            },
          ]
        : []),
    ],
    [isManageable],
  );

  return (
    <div style={{ padding: "8px 16px" }}>
      {isManageable && (
        <div role="toolbar" style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
          <Button
            styleType="default"
            size="small"
            disabled={isBulkOperationDisabled}
            onClick={() => { void handleMute(true); }}
          >
            Mute selected
          </Button>
          <Button
            styleType="default"
            size="small"
            disabled={isBulkOperationDisabled}
            onClick={() => { void handleMute(false); }}
          >
            Unmute selected
          </Button>
          <Button
            styleType="default"
            size="small"
            disabled={isBulkOperationDisabled}
            onClick={handleDelete}
          >
            Delete selected
          </Button>
        </div>
      )}

      <Table
        columns={columns}
        data={rows}
        emptyTableContent="No project run records for this client."
        density="extra-condensed"
        styleType="zebra-rows"
        isSelectable={isManageable}
        onSelect={isManageable ? onRowsSelect : undefined}
        pageSize={25}
        rowProps={(row) => ({
          key: rowKey(row.original),
        })}
      />
    </div>
  );
};

export default ProjectRunsManagementTable;
